import { useState, useEffect, useMemo, useCallback } from 'react'
import { Row, Col, Select, Modal, Button, Input, Popconfirm, Tag } from 'antd'
import InputNumberDiwa from '../../Generic/InputNumberDiwa'
import { CloseOutlined } from '@ant-design/icons'
import { TableColumnTranslationPrefix } from '../../../Constants/Configuration'
import { ArticleColumn } from '../../../Constants/TableConfiguration'
import { hideEditModal, setEditContainerTags, toggleEditTable } from '../../../Slices/Kiosk/TableOptionsSlice'
import { formatString, setCapitalLetter } from '../../../Helpers/StringHelpers'
import { TranslationKeys } from '../../../Constants/TranslationKeys'
import { handleErrorCatchLog, renderTranslation, stopPropagation } from '../../../Helpers/GenericHelpers'
import { updateArticles, populateTableWithArticles } from '../../../Services/Kiosk/ArticleServices'
import { getAutocompleteResults, getFieldAutocompleteResults } from '../../../Services/Kiosk/AutocompleteService'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import ArticleTagContainer from './ArticleTagContainer'

function EditColumsModal() {
  const dispatch = useAppDispatch()
  const editModalInfo = useAppSelector(state => state.present.tableOptions.editModalInfo)
  const rowKeysSelected = useAppSelector(state => state.present.article.rowKeysSelected)
  const language = useAppSelector(state => state.present.language.value)
  const fullEditSelection = useAppSelector(state => state.present.tableOptions.fullEditSelection)

  const [selectedValue, setSelectedValue] = useState('')
  const [searchedValue, setSearchedValue] = useState('')
  const [options, setOptions] = useState([])

  const isEditColumnToBeConfirmed = useMemo(() => {
    return (
      editModalInfo.columnName === ArticleColumn.Brand ||
      editModalInfo.columnName === ArticleColumn.SubCategory ||
      editModalInfo.columnName === ArticleColumn.Department ||
      editModalInfo.columnName === ArticleColumn.ProductDescription
    )
  }, [editModalInfo.columnName])

  const isPriceEditColumn = useMemo(() => {
    return (
      editModalInfo.columnName === ArticleColumn.RetailPricelist1Key ||
      editModalInfo.columnName === ArticleColumn.RetailPricelist2Key ||
      editModalInfo.columnName === ArticleColumn.DiscountedPricelist1Key ||
      editModalInfo.columnName === ArticleColumn.DiscountedPricelist2Key ||
      editModalInfo.columnName === ArticleColumn.Discounted1Pricelist1Key ||
      editModalInfo.columnName === ArticleColumn.Discounted1Pricelist2Key ||
      editModalInfo.columnName === ArticleColumn.Discounted2Pricelist1Key ||
      editModalInfo.columnName === ArticleColumn.Discounted2Pricelist2Key
    )
  }, [editModalInfo.columnName])

  const isTagColumn = useMemo(() => {
    return editModalInfo.columnName === ArticleColumn.Tag
  }, [editModalInfo.columnName])

  const [inputValue, setInputValue] = useState(isPriceEditColumn ? 0 : '')

  useEffect(() => {
    if (editModalInfo.showModal) {
      if (
        editModalInfo.columnName !== ArticleColumn.ProductDescription &&
        !isPriceEditColumn &&
        editModalInfo.columnName !== ArticleColumn.Tag
      ) {
        getFieldAutocompleteResults(editModalInfo.columnName, searchedValue).then(data => {
          setOptions(
            data && Array.isArray(data)
              ? data.map((item, index) => {
                  return { key: index, value: item, label: item }
                })
              : [],
          )
        })
      }
    }
  }, [editModalInfo.columnName, editModalInfo.showModal, isPriceEditColumn, searchedValue])

  const hideModal = useCallback(() => {
    dispatch(hideEditModal())
    setInputValue('')
    setSelectedValue('')
    if (options.length !== 0) {
      setOptions([])
    }

    if (isTagColumn) {
      dispatch(setEditContainerTags([]))
    }
  }, [dispatch, options.length, isTagColumn])

  const sendEditValue = useCallback(() => {
    // if (!isTagColumn) {
    const valueToSend =
      editModalInfo.columnName === ArticleColumn.ProductDescription || isPriceEditColumn ? inputValue : selectedValue
    updateArticles(editModalInfo.columnName, valueToSend)
      .then(response => {
        dispatch(populateTableWithArticles())
        dispatch(toggleEditTable())
      })
      .catch(err => {
        handleErrorCatchLog(err, 'Error on update articles sendEditValue')
      })
    hideModal()
  }, [editModalInfo.columnName, hideModal, inputValue, isPriceEditColumn, selectedValue, isTagColumn])

  const onEditInputChange = useCallback(e => {
    setInputValue(e.target.value)
  }, [])

  const onSelectValueChange = useCallback(value => {
    setSelectedValue(value)
  }, [])

  const confirmSpecialEdit = useCallback(
    e => {
      stopPropagation(e)
      setTimeout(sendEditValue, 0)
    },
    [sendEditValue],
  )

  const onPriceEditChange = useCallback(value => {
    setInputValue(value)
  }, [])

  let title = useMemo(() => {
    return renderTranslation(language, TranslationKeys.editModalTitle)
  }, [language])
  const columnNameForTranslation = useMemo(() => {
    return !isPriceEditColumn
      ? editModalInfo.columnName
      : editModalInfo.columnName === ArticleColumn.RetailPricelist1Key ||
        editModalInfo.columnName === ArticleColumn.RetailPricelist2Key
      ? ArticleColumn.RetailPrice
      : editModalInfo.columnName === ArticleColumn.DiscountedPricelist1Key ||
        editModalInfo.columnName === ArticleColumn.DiscountedPricelist2Key
      ? ArticleColumn.DiscountedPrice
      : editModalInfo.columnName === ArticleColumn.Discounted1Pricelist1Key ||
        editModalInfo.columnName === ArticleColumn.Discounted1Pricelist2Key
      ? ArticleColumn.DiscountedPrice1
      : ArticleColumn.DiscountedPrice2
  }, [editModalInfo.columnName, isPriceEditColumn])

  const columnName = useMemo(() => {
    return renderTranslation(
      language,
      TranslationKeys[TableColumnTranslationPrefix + setCapitalLetter(columnNameForTranslation)],
    )
  }, [columnNameForTranslation, language])

  useEffect(() => {
    setInputValue(isPriceEditColumn ? 0 : '')
  }, [editModalInfo])

  return (
    <Modal
      open={editModalInfo.showModal}
      footer={null}
      onCancel={hideModal}
      style={{ top: '64px' }}
      width={500}
      destroyOnClose={true}
    >
      <Row className='modal-edit-container'>
        <Col span={24} className='edit-title'>
          <span
            dangerouslySetInnerHTML={{
              __html: formatString(
                title,
                "<span class='bold'>" + columnName + '</span>',
                "<span class='bold'>" +
                  (fullEditSelection
                    ? renderTranslation(language, TranslationKeys.editResultFullSelect)
                    : rowKeysSelected.length) +
                  '</span>',
              ),
            }}
          ></span>
        </Col>
        <Col span={24} className='edit-body'>
          {editModalInfo.columnName === ArticleColumn.ProductDescription ? (
            <Input
              placeholder={renderTranslation(language, TranslationKeys.editModalInputPlaceholder)}
              onChange={onEditInputChange}
              allowClear={{ clearIcon: <CloseOutlined className='general-icon input-clear-icon' /> }}
            />
          ) : isTagColumn ? (
            <ArticleTagContainer populateData={editModalInfo.showModal} />
          ) : !isPriceEditColumn ? (
            <Select
              value={selectedValue}
              showSearch={true}
              onSearch={setSearchedValue}
              onChange={onSelectValueChange}
              className='edit-select'
              optionFilterProp='label'
              options={options.map(element => {
                return {
                  label: element.label,
                  value: element.value,
                }
              })}
            />
          ) : (
            <InputNumberDiwa min={0} controls={false} value={inputValue} onChange={onPriceEditChange} />
          )}
        </Col>
        {isEditColumnToBeConfirmed ? (
          <Col span={24} className='edit-footer'>
            <Popconfirm
              title={renderTranslation(language, TranslationKeys.editModalSpecialEditModelMessage)}
              onConfirm={confirmSpecialEdit}
              onCancel={stopPropagation}
              okText={renderTranslation(language, TranslationKeys.configurationDropdownPopYes)}
              cancelText={renderTranslation(language, TranslationKeys.configurationDropdownPopNo)}
            >
              <Button onClick={stopPropagation} type='primary' className='edit-button' block>
                {renderTranslation(language, TranslationKeys.editModalButtonText)}
              </Button>
            </Popconfirm>
          </Col>
        ) : (
          <Col span={24} className='edit-footer'>
            <Button type='primary' className='edit-button' onClick={sendEditValue} block>
              {renderTranslation(language, TranslationKeys.editModalButtonText)}
            </Button>
          </Col>
        )}
      </Row>
    </Modal>
  )
}

export default EditColumsModal
